import React, { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { setCrmSessionActor, setCrmSessionToken } from '../crm/utils/crmEnv';
import { portalApiUrl } from './portalApi';
import '../crm/crm.css';

const PortalLogin = () => {
    const history = useHistory();
    const [aliasForm, setAliasForm] = useState({ alias: '', pin: '' });
    const [legacyForm, setLegacyForm] = useState({ franchiseId: '', seatId: '', pin: '' });
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [showReplaced, setShowReplaced] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    useEffect(() => {
        if (typeof window === 'undefined') return;
        const replaced = window.localStorage.getItem('crmSessionReplaced');
        if (replaced) {
            window.localStorage.removeItem('crmSessionReplaced');
            setShowReplaced(true);
        }
    }, []);

    const handleAliasChange = (field) => (event) => {
        setAliasForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const handleLegacyChange = (field) => (event) => {
        setLegacyForm((prev) => ({ ...prev, [field]: event.target.value }));
    };

    const getDeviceId = () => {
        if (typeof window === 'undefined') return '';
        const key = 'crmPortalDeviceId';
        let value = window.localStorage.getItem(key);
        if (!value && window.crypto?.randomUUID) {
            value = window.crypto.randomUUID();
            window.localStorage.setItem(key, value);
        }
        return value || '';
    };

    const handleAliasSubmit = async (event) => {
        event.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(portalApiUrl('/api/crm-portal/portal/login-alias'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    alias: aliasForm.alias.trim(),
                    pin: aliasForm.pin,
                    deviceId: getDeviceId(),
                }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.error || data.message || 'Login failed');
            }

            setCrmSessionToken(data.sessionToken);
            setCrmSessionActor({
                seatId: data?.actor?.seatId || data?.seatId || null,
                name: data?.actor?.name || data?.name || aliasForm.alias.trim() || 'Teammate',
                role: data?.actor?.role || data?.role || 'agent',
                franchiseId: data?.franchiseId || null,
            });
            history.push('/crm-portal/app');
        } catch (err) {
            setError(err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    const handleLegacySubmit = async (event) => {
        event.preventDefault();
        setError('');
        setLoading(true);

        try {
            const response = await fetch(portalApiUrl('/api/crm-portal/portal/login'), {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({
                    franchiseId: legacyForm.franchiseId.trim(),
                    seatId: legacyForm.seatId.trim(),
                    pin: legacyForm.pin,
                    deviceId: getDeviceId(),
                }),
            });

            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.error || data.message || 'Login failed');
            }

            setCrmSessionToken(data.sessionToken);
            setCrmSessionActor({
                seatId: data?.actor?.seatId || legacyForm.seatId.trim() || null,
                name: data?.actor?.name || data?.name || 'Teammate',
                role: data?.actor?.role || data?.role || 'agent',
                franchiseId: data?.franchiseId || legacyForm.franchiseId.trim() || null,
            });
            history.push('/crm-portal/app');
        } catch (err) {
            setError(err.message || 'Login failed');
        } finally {
            setLoading(false);
        }
    };

    return (
        <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--crm-bg-secondary)', padding: '24px' }}>
            <div className="crm-card" style={{ width: '100%', maxWidth: '420px' }}>
                <div className="crm-card-header">
                    <h2 className="crm-card-title">CRM Portal Login</h2>
                </div>
                <div className="crm-card-body">
                    {error && (
                        <div style={{ marginBottom: '16px', color: 'var(--crm-danger)' }}>
                            {error}
                        </div>
                    )}
                    <form onSubmit={handleAliasSubmit} style={{ display: 'grid', gap: '16px' }}>
                        <div>
                            <label className="crm-label">Username</label>
                            <input
                                className="crm-input"
                                value={aliasForm.alias}
                                onChange={handleAliasChange('alias')}
                                required
                            />
                        </div>
                        <div>
                            <label className="crm-label">PIN</label>
                            <input
                                className="crm-input"
                                type="password"
                                value={aliasForm.pin}
                                onChange={handleAliasChange('pin')}
                                required
                            />
                        </div>
                        <button className="crm-btn crm-btn-primary" type="submit" disabled={loading}>
                            {loading ? 'Signing in...' : 'Sign In'}
                        </button>
                    </form>
                    <div style={{ marginTop: '16px' }}>
                        <button
                            type="button"
                            className="crm-btn crm-btn-secondary"
                            onClick={() => setShowAdvanced((prev) => !prev)}
                        >
                            {showAdvanced ? 'Hide Advanced' : 'Advanced Login'}
                        </button>
                    </div>
                    {showAdvanced && (
                        <form onSubmit={handleLegacySubmit} style={{ display: 'grid', gap: '16px', marginTop: '16px' }}>
                            <div>
                                <label className="crm-label">Franchise ID</label>
                                <input
                                    className="crm-input"
                                    value={legacyForm.franchiseId}
                                    onChange={handleLegacyChange('franchiseId')}
                                    required
                                />
                            </div>
                            <div>
                                <label className="crm-label">Seat ID</label>
                                <input
                                    className="crm-input"
                                    value={legacyForm.seatId}
                                    onChange={handleLegacyChange('seatId')}
                                    required
                                />
                            </div>
                            <div>
                                <label className="crm-label">PIN</label>
                                <input
                                    className="crm-input"
                                    type="password"
                                    value={legacyForm.pin}
                                    onChange={handleLegacyChange('pin')}
                                    required
                                />
                            </div>
                            <button className="crm-btn crm-btn-primary" type="submit" disabled={loading}>
                                {loading ? 'Signing in...' : 'Sign In (Legacy)'}
                            </button>
                        </form>
                    )}
                </div>
            </div>
            {showReplaced && (
                <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px', zIndex: 60 }}>
                    <div className="crm-card" style={{ width: '100%', maxWidth: '420px' }}>
                        <div className="crm-card-header">
                            <h3 className="crm-card-title">Signed Out</h3>
                        </div>
                        <div className="crm-card-body">
                            <p style={{ color: 'var(--crm-gray-600)', marginBottom: '16px' }}>
                                You were signed out because this account was opened on another device.
                            </p>
                            <button className="crm-btn crm-btn-primary" type="button" onClick={() => setShowReplaced(false)}>
                                OK
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default PortalLogin;
